import { index, modelOptions, mongoose, post, prop } from "@typegoose/typegoose";
import { z } from "zod";
import { ObjectIdString, StoredVerdiIdString } from "../../validators/index.js";
import { getZodSchemaFromClass, mongooseValidatorFromZod, validate } from "../../zod-decorator/index.js";
import { ScdDocument } from "../scdDocument/scdDocument.js";
import { addSchemaKeyHook, getDefaultSchemaOptions, SchemaBase, SchemaConstructorArgs } from "../schemaBase.js";
import type { LockoutBannerFieldsWithDefaults, LockoutBannerSchemaTypes } from "../types/lockoutBanner.d.js";
import { DtoValidatorKeys, type DtoValidatorTypes } from "../validatableClass.js";

const SCHEMA_KEY = "lockoutBanners" as const;

// Not z.iso.date(): its `format: date` makes the generated client type the field as a Date, but it arrives as a string.
const CalendarDateString = z
    .string()
    .refine((value) => z.iso.date().validate(value), { message: "Expected a YYYY-MM-DD date" })
    .openapi({ pattern: "^\\d{4}-\\d{2}-\\d{2}$", example: "2026-11-30" });

export enum LockoutBannerSeverity {
    CRITICAL = "CRITICAL",
    WARNING = "WARNING",
}

export enum LockoutBannerMessageKey {
    FIRST_INSTALMENT_DUE = "FIRST_INSTALMENT_DUE",
    SECOND_INSTALMENT_DUE = "SECOND_INSTALMENT_DUE",
    PAYMENT_DUE = "PAYMENT_DUE",
    PAYMENT_OVERDUE = "PAYMENT_OVERDUE",
    TERMINATION_RISK = "TERMINATION_RISK",
}

/** One per organization, keyed on its verdiId. SCD Type-2: each edit is a new version. */
@index(
    { organizationVerdiId: 1 },
    { unique: true, partialFilterExpression: { versionIsCurrent: true, deleted: false } },
)
@modelOptions({
    schemaOptions: {
        ...getDefaultSchemaOptions(SCHEMA_KEY),
        collection: "lockoutBanners" as const satisfies typeof SCHEMA_KEY,
        _id: true,
    },
})
@post<SchemaBase<LockoutBanner>>(...addSchemaKeyHook(SCHEMA_KEY))
export class LockoutBanner extends ScdDocument<LockoutBanner, LockoutBannerFieldsWithDefaults> {
    static readonly schemaKey = SCHEMA_KEY;

    @validate({ schemaKey: SCHEMA_KEY })
    readonly schemaKey = SCHEMA_KEY;

    @validate({ zodValidator: ObjectIdString, responseOnly: true })
    public _id!: mongoose.Types.ObjectId;

    @validate({ zodValidator: StoredVerdiIdString, responseOnly: true })
    @prop({ type: () => String, required: true, validate: mongooseValidatorFromZod(StoredVerdiIdString) })
    public organizationVerdiId!: string;

    @validate({ zodValidator: z.boolean() })
    @prop({ type: () => Boolean, required: true })
    public enabled!: boolean;

    @validate({ zodValidator: z.enum(LockoutBannerSeverity) })
    @prop({ type: () => String, enum: LockoutBannerSeverity, required: true })
    public severity!: LockoutBannerSeverity;

    @validate({ zodValidator: z.enum(LockoutBannerMessageKey).optional() })
    @prop({ type: () => String, enum: LockoutBannerMessageKey, required: false })
    public messageKey?: LockoutBannerMessageKey;

    @validate({ zodValidator: z.string().trim().min(1).max(1000).optional() })
    @prop({ type: () => String, required: false })
    public customMessage?: string;

    /** The due date, shown by preset messages. */
    @validate({ zodValidator: CalendarDateString.optional() })
    @prop({ type: () => String, required: false })
    public date?: string;

    /** Rendered as an href, so http(s) only. */
    @validate({ zodValidator: z.httpUrl().max(2048) })
    @prop({ type: () => String, required: true })
    public linkUrl!: string;

    static getters = {} as const satisfies Record<string, (self: LockoutBanner) => any>;

    static utilities = {} as const satisfies Record<string, (...args: any) => any>;

    static dtoValidators: DtoValidatorTypes<LockoutBannerSchemaTypes>;

    constructor(properties?: SchemaConstructorArgs<LockoutBanner, LockoutBannerFieldsWithDefaults>) {
        super(properties);
        Object.assign(this, properties);
    }
}

LockoutBanner.dtoValidators = getZodSchemaFromClass<LockoutBanner>(LockoutBanner, {
    superRefine: (banner, ctx, variant) => {
        if (variant === DtoValidatorKeys.Update) return;
        if ((banner.messageKey === undefined) === (banner.customMessage === undefined)) {
            ctx.addIssue({ code: "custom", message: "Set exactly one of messageKey and customMessage" });
        }
    },
}) as unknown as DtoValidatorTypes<LockoutBannerSchemaTypes>;
